import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Camera, Play, Square, Wifi, WifiOff, Zap, Settings, AlertCircle, CheckCircle, Clock, Car, X, Bell, BellOff, Video, Monitor, Webcam } from 'lucide-react'

const YOLO_CAMERA_SOURCE = 'browser'
const isVirtualCamera = (camera) => /droid\s*cam|obs\s*virtual/i.test(camera?.label || '')
const mapVideoDevices = (devices) => devices
    .filter(device => device.kind === 'videoinput')
    .map((device, index) => ({
        deviceId: device.deviceId,
        label: device.label || `Camera ${index + 1}`,
        index: index.toString()
    }))
const findPreferredCamera = (cameras) => cameras.find(camera => !isVirtualCamera(camera)) || cameras[0]
const findSecondaryCamera = (cameras, primary) =>
    cameras.find(camera => camera.deviceId !== primary?.deviceId && !isVirtualCamera(camera))
    || cameras.find(camera => camera.deviceId !== primary?.deviceId && /droid\s*cam\s*video/i.test(camera.label))
    || cameras.find(camera => camera.deviceId !== primary?.deviceId)
    || primary

function useVideoSocket(serverUrl, roomId, maxFps = 25) {
    const [socket, setSocket] = useState(null);
    const [isConnected, setIsConnected] = useState(false);

    const [receivedFrame, setReceivedFrame] = useState(null);
    const [detections, setDetections] = useState([]);
    const [arrival, setArrival] = useState(null);

    const [inferenceMs, setInferenceMs] = useState(null);
    const [detectionCount, setDetectionCount] = useState(0);
    const [status, setStatus] = useState(null);
    const [lastError, setLastError] = useState(null);

    const fpsGateRef = useRef(0);

    useEffect(() => {
        let activeSocket = null;
        let disposed = false;

        const loadSocketIO = () => {
            return new Promise((resolve, reject) => {
                if (window.io) {
                    resolve(window.io);
                    return;
                }

                const script = document.createElement('script');
                script.src = 'https://cdnjs.cloudflare.com/ajax/libs/socket.io/4.7.2/socket.io.js';
                script.onload = () => resolve(window.io);
                script.onerror = reject;
                document.head.appendChild(script);
            });
        };

        loadSocketIO().then((io) => {
            if (disposed) return;

            const s = io(serverUrl, {
                transports: ['polling', 'websocket'],
                timeout: 10000,
                reconnection: true,
                reconnectionAttempts: 5,
                reconnectionDelay: 2000,
            });
            activeSocket = s;
            setSocket(s);

            s.on('connect', () => {
                setIsConnected(true);
                s.emit('join', roomId);
            });

            s.on('disconnect', () => {
                setIsConnected(false);
                setReceivedFrame(null);
            });

            s.on('connect_error', (err) => {
                setIsConnected(false);
                setLastError({ msg: String(err?.message || err) });
            });

            const onStatus = (payload) => {
                if (payload?.room_id && payload.room_id !== roomId) return;
                setStatus(payload);
            };

            const onError = (payload) => {
                if (payload?.room_id && payload.room_id !== roomId) return;
                setLastError(payload);
                console.error('Server error:', payload);
            };

            const onFrame = (payload) => {
                const now = performance.now();
                const minInterval = 1000 / Math.max(1, maxFps);
                if (now - fpsGateRef.current < minInterval) return;
                fpsGateRef.current = now;

                if (payload?.image) {
                    setReceivedFrame(`data:image/jpeg;base64,${payload.image}`);
                }
            };

            const onDetections = (payload) => {
                if (payload?.room_id && payload.room_id !== roomId) return;
                setDetections(payload?.detections || []);
                setDetectionCount(payload?.detection_count ?? 0);
                setInferenceMs(payload?.inference_ms ?? null);
            };

            const onArrival = (payload) => {
                if (payload?.room_id && payload.room_id !== roomId) return;
                setArrival(payload);
                setTimeout(() => setArrival(null), 5000);
                if (payload?.inference_ms != null) setInferenceMs(payload.inference_ms);
            };

            s.on('status', onStatus);
            s.on('error', onError);
            s.on('frame', onFrame);
            s.on('detections', onDetections);
            s.on('vehicle_arrival', onArrival);
            s.on('pong', (d) => console.log('pong:', d));

        }).catch((error) => {
            if (disposed) return;
            console.error('Failed to load Socket.IO:', error);
            setLastError({ msg: 'Failed to load Socket.IO library' });
        });

        return () => {
            disposed = true;
            if (activeSocket) {
                activeSocket.removeAllListeners();
                activeSocket.disconnect();
            }
        };
    }, [serverUrl, roomId, maxFps]);

    return {
        socket,
        isConnected,
        receivedFrame,
        setReceivedFrame,
        detections,
        arrival,
        status,
        lastError,
        inferenceMs,
        detectionCount,
    };
}

const requestNotificationPermission = async () => {
    if (!("Notification" in window)) return false;
    if (Notification.permission === "granted") return true;
    if (Notification.permission !== "denied") {
        const permission = await Notification.requestPermission();
        return permission === "granted";
    }
    return false;
};

const showNotification = (title, options = {}) => {
    if (Notification.permission === "granted") {
        const notification = new Notification(title, {
            icon: "/favicon.ico",
            badge: "/badge-icon.png",
            requireInteraction: true,
            ...options
        });
        setTimeout(() => notification.close(), 10000);
        return notification;
    }
    return null;
};

export default function SmartGateSystem({
    serverUrl = 'http://localhost:8000',
    plateDetectionUrl = serverUrl
}) {
    const localVideoRef = useRef(null)
    const yoloVideoRef = useRef(null)
    const canvasRef = useRef(null)
    const streamRef = useRef(null)
    const yoloStreamRef = useRef(null)
    const cameraRequestRef = useRef(0)

    const [roomId] = useState(() => `room_${Date.now()}`)
    const [isLocalStreaming, setIsLocalStreaming] = useState(false)
    const [isServerStreaming, setIsServerStreaming] = useState(false)
    const [error, setError] = useState(null)
    const [totalDetections, setTotalDetections] = useState(0)
    const [lastInferenceTime, setLastInferenceTime] = useState(0)
    const [showSettings, setShowSettings] = useState(false)

    const [availableCameras, setAvailableCameras] = useState([])
    const [selectedPreviewCamera, setSelectedPreviewCamera] = useState('0')
    const [selectedCamera, setSelectedCamera] = useState('0')
    const [showPreviewCameraSelector, setShowPreviewCameraSelector] = useState(false)
    const [showYoloCameraSelector, setShowYoloCameraSelector] = useState(false)

    const [notificationsEnabled, setNotificationsEnabled] = useState(false)
    const [notificationPermission, setNotificationPermission] = useState(Notification?.permission || 'default')
    const [notificationHistory, setNotificationHistory] = useState([])

    const [catchEventHistory, setCatchEventHistory] = useState([])
    const [showCatchEventModal, setShowCatchEventModal] = useState(false)
    const [latestCatchEvent, setLatestCatchEvent] = useState(null)

    const [isDetectingPlate, setIsDetectingPlate] = useState(false)
    const [lastPlateResult, setLastPlateResult] = useState(null)

    const [bestFrameBuffer, setBestFrameBuffer] = useState({ frame: null, confidence: 0, timestamp: 0 })
    const [isCapturingWindow, setIsCapturingWindow] = useState(false)
    const [lastCaptureTime, setLastCaptureTime] = useState(0)
    const capturingWindowRef = useRef(null)

    const [config, setConfig] = useState({
        maxFps: 25,
        confidence: 0.25,
        enableSound: true,
        notificationTimeout: 10000,
        enableCatchEventAlert: true,
        syncMode: true,
        autoDetectPlate: true,
        minConfidenceForCapture: 0.65,
        captureWindowDuration: 2500,
        captureCooldown: 8000
    })

    const {
        socket, isConnected, receivedFrame, setReceivedFrame,
        detections, arrival, status, lastError, inferenceMs, detectionCount
    } = useVideoSocket(serverUrl, roomId, config.maxFps);

    const captureAndDetectPlate = useCallback(async (sourceInfo = 'manual') => {
        if (!receivedFrame) {
            console.warn('Camera YOLO chưa có frame để chụp ảnh');
            return null;
        }

        console.log('📸 Đang chụp ảnh từ Camera Phải (YOLO stream) - Trigger:', sourceInfo);
        setIsDetectingPlate(true);

        try {
            // Convert base64 to blob
            const base64Data = receivedFrame.split(',')[1];
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'image/jpeg' });

            const formData = new FormData();
            formData.append("image", blob, "capture.jpg");

            try {
                const res = await fetch(`${plateDetectionUrl}/detect`, {
                    method: "POST",
                    body: formData,
                });

                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }

                const data = await res.json();
                console.log('Plate detection result:', data);
                setLastPlateResult(data);
                setIsDetectingPlate(false);
                return data;
            } catch (err) {
                console.error("Lỗi khi detect biển số:", err);
                setIsDetectingPlate(false);
                return null;
            }
        } catch (err) {
            console.error("Lỗi khi convert ảnh:", err);
            setIsDetectingPlate(false);
            return null;
        }
    }, [receivedFrame, plateDetectionUrl]);

    useEffect(() => {
        const getCameras = async () => {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const cameras = mapVideoDevices(devices)
                setAvailableCameras(cameras)
                const previewCamera = findPreferredCamera(cameras)
                const yoloCamera = findSecondaryCamera(cameras, previewCamera)
                if (previewCamera) setSelectedPreviewCamera(previewCamera.index)
                if (yoloCamera) setSelectedCamera(yoloCamera.index)
            } catch (error) {
                console.error('Error getting cameras:', error);
                setAvailableCameras([])
                setError(error.message || 'Không thể đọc danh sách camera.')
            }
        };
        getCameras();
    }, []);

    useEffect(() => {
        if ("Notification" in window) {
            setNotificationPermission(Notification.permission);
            if (Notification.permission === "granted") setNotificationsEnabled(true);
        }
    }, []);

    const runCatchEvent = useCallback(async (eventData) => {
        console.log('🚗 runCatchEvent triggered - Xe phát hiện ở Cam Phải (YOLO):', eventData);

        // Cooldown check
        const now = Date.now();
        if (now - lastCaptureTime < config.captureCooldown) {
            console.log(`⏳ Cooldown active. Skipping capture. (${((config.captureCooldown - (now - lastCaptureTime)) / 1000).toFixed(1)}s remaining)`);
            return;
        }

        // Get max confidence from current detections
        const maxConfidence = eventData.detections?.reduce((max, det) => Math.max(max, det.confidence || 0), 0) || 0;
        console.log(`📊 Current max confidence: ${(maxConfidence * 100).toFixed(1)}%`);

        // Start capture window if not already active
        if (!isCapturingWindow && config.autoDetectPlate && receivedFrame) {
            console.log(`🎬 Opening capture window for ${config.captureWindowDuration}ms...`);
            setIsCapturingWindow(true);
            setBestFrameBuffer({ frame: receivedFrame, confidence: maxConfidence, timestamp: now });

            // Set timeout to capture best frame
            capturingWindowRef.current = setTimeout(async () => {
                const bestFrame = bestFrameBuffer.frame || receivedFrame;
                const bestConf = bestFrameBuffer.confidence || maxConfidence;

                console.log(`🎯 Capture window closed. Best confidence: ${(bestConf * 100).toFixed(1)}%`);

                if (bestConf >= config.minConfidenceForCapture) {
                    console.log('✅ Confidence threshold met. Capturing plate...');
                    const plateInfo = await captureAndDetectPlateFromFrame(bestFrame);

                    const eventInfo = {
                        id: Date.now(),
                        timestamp: new Date().toLocaleTimeString('vi-VN'),
                        fullTimestamp: new Date().toLocaleString('vi-VN'),
                        vehicleCount: eventData.count || 0,
                        detections: eventData.detections || [],
                        inferenceTime: eventData.inference_ms || 0,
                        roomId: eventData.room_id || roomId,
                        plate: plateInfo?.plate || null,
                        plateImage: plateInfo?.image || null,
                        captureConfidence: bestConf,
                        rawData: eventData
                    };

                    setLatestCatchEvent(eventInfo);
                    setCatchEventHistory(prev => [eventInfo, ...prev.slice(0, 9)]);

                    const vehicleTypes = eventData.detections?.map(d => d.class_name).join(", ") || "xe không xác định";
                    const plateText = plateInfo?.plate ? `\nBiển số: ${plateInfo.plate}` : '\nBiển số: Không phát hiện';

                    const alertMessage = `PHÁT HIỆN XE VÀO!\n\n` +
                        `Thời gian: ${eventInfo.fullTimestamp}\n` +
                        `Số lượng: ${eventInfo.vehicleCount} chiếc\n` +
                        `Loại xe: ${vehicleTypes}` +
                        plateText + `\n` +
                        `Độ tin cậy: ${(bestConf * 100).toFixed(1)}%\n` +
                        `Thời gian xử lý: ${eventInfo.inferenceTime.toFixed(2)}ms\n` +
                        `Camera YOLO: ${parseInt(selectedCamera) + 1}\n` +
                        `Ảnh biển từ: Cam Phải (YOLO frame)\n` +
                        `Room: ${eventInfo.roomId}`;

                    if (config.enableCatchEventAlert) {
                        alert(alertMessage);
                    }

                    setShowCatchEventModal(true);

                    if (config.enableSound) {
                        playVehicleArrivalSound();
                    }

                    setLastCaptureTime(Date.now());
                } else {
                    console.log(`❌ Confidence too low (${(bestConf * 100).toFixed(1)}% < ${(config.minConfidenceForCapture * 100).toFixed(0)}%). Skipping capture.`);
                }

                setIsCapturingWindow(false);
                setBestFrameBuffer({ frame: null, confidence: 0, timestamp: 0 });
            }, config.captureWindowDuration);
        } else if (isCapturingWindow && maxConfidence > bestFrameBuffer.confidence) {
            // Update best frame if current confidence is higher
            console.log(`📈 New best frame found: ${(maxConfidence * 100).toFixed(1)}% (previous: ${(bestFrameBuffer.confidence * 100).toFixed(1)}%)`);
            setBestFrameBuffer({ frame: receivedFrame, confidence: maxConfidence, timestamp: now });
        }
    }, [config.autoDetectPlate, config.enableCatchEventAlert, config.enableSound,
    config.minConfidenceForCapture, config.captureWindowDuration, config.captureCooldown,
        receivedFrame, roomId, selectedCamera, isCapturingWindow, bestFrameBuffer, lastCaptureTime]);

    const captureAndDetectPlateFromFrame = useCallback(async (frameData) => {
        if (!frameData) {
            console.warn('Không có frame để chụp ảnh');
            return null;
        }

        console.log('📸 Đang detect biển số từ frame đã chọn...');
        setIsDetectingPlate(true);

        try {
            const base64Data = frameData.split(',')[1];
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'image/jpeg' });

            const formData = new FormData();
            formData.append("image", blob, "capture.jpg");

            try {
                const res = await fetch(`${plateDetectionUrl}/detect`, {
                    method: "POST",
                    body: formData,
                });

                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }

                const data = await res.json();
                console.log('Plate detection result:', data);
                setLastPlateResult(data);
                setIsDetectingPlate(false);
                return data;
            } catch (err) {
                console.error("Lỗi khi detect biển số:", err);
                setIsDetectingPlate(false);
                return null;
            }
        } catch (err) {
            console.error("Lỗi khi convert ảnh:", err);
            setIsDetectingPlate(false);
            return null;
        }
    }, [plateDetectionUrl]);

    const playVehicleArrivalSound = () => {
        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const beepTimes = [0, 0.2, 0.4];
            beepTimes.forEach((time) => {
                const oscillator = audioContext.createOscillator();
                const gainNode = audioContext.createGain();
                oscillator.connect(gainNode);
                gainNode.connect(audioContext.destination);
                oscillator.frequency.value = 1000;
                oscillator.type = 'square';
                gainNode.gain.setValueAtTime(0.5, audioContext.currentTime + time);
                gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + time + 0.1);
                oscillator.start(audioContext.currentTime + time);
                oscillator.stop(audioContext.currentTime + time + 0.1);
            });
        } catch (e) {
            console.warn("Could not play vehicle arrival sound:", e);
        }
    };

    const toggleNotifications = async () => {
        if (!("Notification" in window)) {
            alert("Browser này không hỗ trợ thông báo desktop");
            return;
        }
        if (notificationPermission === "denied") {
            alert("Thông báo đã bị từ chối. Vui lòng bật lại trong cài đặt browser.");
            return;
        }
        if (!notificationsEnabled) {
            const granted = await requestNotificationPermission();
            if (granted) {
                setNotificationsEnabled(true);
                setNotificationPermission("granted");
                showNotification("SmartGate Thông báo đã bật!", {
                    body: "Bạn sẽ nhận được thông báo khi phát hiện xe"
                });
            } else {
                setNotificationPermission(Notification.permission);
            }
        } else {
            setNotificationsEnabled(false);
        }
    };

    const playNotificationSound = () => {
        if (!config.enableSound) return;
        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            oscillator.frequency.value = 800;
            oscillator.type = 'sine';
            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
            oscillator.start();
            oscillator.stop(audioContext.currentTime + 0.5);
        } catch (e) {
            console.warn("Could not play notification sound:", e);
        }
    };

    useEffect(() => {
        if (!arrival) return;

        runCatchEvent(arrival);

        if (notificationsEnabled) {
            const vehicleTypes = arrival.detections?.map(d => d.class_name).join(", ") || "xe";
            const message = `Phát hiện ${arrival.count} ${vehicleTypes} đang đến gần cổng!`;

            const notification = showNotification("SmartGate - Xe đang tới!", {
                body: message,
                tag: "vehicle-arrival",
            });

            playNotificationSound();

            const historyItem = {
                id: Date.now(),
                timestamp: new Date().toLocaleTimeString('vi-VN'),
                message,
                count: arrival.count,
                detections: arrival.detections?.slice(0, 3) || []
            };
            setNotificationHistory(prev => [historyItem, ...prev.slice(0, 9)]);

            if (notification) {
                notification.onclick = () => {
                    window.focus();
                    notification.close();
                };
            }
        }
    }, [arrival, notificationsEnabled, config.enableSound, runCatchEvent]);

    useEffect(() => {
        if (socket && isConnected) {
            socket.emit('join', roomId);
            const onStatus = (data) => {
                if (data?.msg?.includes('Joined room')) {
                    console.log('Confirm room join:', roomId);
                }
            };
            socket.on('status', onStatus);
            return () => socket.off('status', onStatus);
        }
    }, [socket, isConnected, roomId]);

    const startLocalCamera = useCallback(async (requestedPreviewIndex = null, requestedYoloIndex = null) => {
        const requestId = ++cameraRequestRef.current
        let permissionStream = null
        let previewStream = null
        let yoloStream = null
        const ensureRequestIsCurrent = () => {
            if (requestId !== cameraRequestRef.current) {
                const cancelledError = new Error('Camera request cancelled')
                cancelledError.name = 'AbortError'
                throw cancelledError
            }
        }

        try {
            if (!navigator.mediaDevices?.getUserMedia || !navigator.mediaDevices?.enumerateDevices) {
                throw new Error('Trình duyệt không hỗ trợ camera. Hãy dùng Chrome hoặc Edge qua HTTPS.')
            }

            setError(null)
            const previewIndex = typeof requestedPreviewIndex === 'string'
                ? requestedPreviewIndex
                : selectedPreviewCamera
            const yoloIndex = typeof requestedYoloIndex === 'string'
                ? requestedYoloIndex
                : selectedCamera

            streamRef.current?.getTracks().forEach(track => track.stop())
            yoloStreamRef.current?.getTracks().forEach(track => track.stop())
            streamRef.current = null
            yoloStreamRef.current = null

            let cameras = mapVideoDevices(await navigator.mediaDevices.enumerateDevices())
            const labelsAreHidden = cameras.length === 0
                || cameras.every(camera => !camera.deviceId || /^Camera \d+$/.test(camera.label))

            if (labelsAreHidden) {
                permissionStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false })
                ensureRequestIsCurrent()
                cameras = mapVideoDevices(await navigator.mediaDevices.enumerateDevices())
                permissionStream.getTracks().forEach(track => track.stop())
                permissionStream = null
            }

            if (cameras.length === 0) {
                throw new Error('Không tìm thấy camera nào trên thiết bị.')
            }

            setAvailableCameras(cameras)
            const previewDevice = cameras.find(camera => camera.index === previewIndex)
                || findPreferredCamera(cameras)
            let yoloDevice = cameras.find(camera => camera.index === yoloIndex)
                || findSecondaryCamera(cameras, previewDevice)

            if (cameras.length > 1 && yoloDevice.deviceId === previewDevice.deviceId) {
                yoloDevice = findSecondaryCamera(cameras, previewDevice)
            }

            const constraintsFor = (device) => ({
                video: {
                    deviceId: { exact: device.deviceId },
                    width: { ideal: 1280, max: 1920 },
                    height: { ideal: 720, max: 1080 },
                    frameRate: { ideal: config.maxFps, max: 60 }
                },
                audio: false
            })

            previewStream = await navigator.mediaDevices.getUserMedia(constraintsFor(previewDevice))
            ensureRequestIsCurrent()
            if (yoloDevice.deviceId === previewDevice.deviceId) {
                yoloStream = previewStream.clone()
            } else {
                yoloStream = await navigator.mediaDevices.getUserMedia(constraintsFor(yoloDevice))
                ensureRequestIsCurrent()
            }

            const activePreviewId = previewStream.getVideoTracks()[0]?.getSettings()?.deviceId
            const activeYoloId = yoloStream.getVideoTracks()[0]?.getSettings()?.deviceId
            const activePreviewCamera = cameras.find(camera => camera.deviceId === activePreviewId) || previewDevice
            const activeYoloCamera = cameras.find(camera => camera.deviceId === activeYoloId) || yoloDevice
            setSelectedPreviewCamera(activePreviewCamera.index)
            setSelectedCamera(activeYoloCamera.index)

            streamRef.current = previewStream
            yoloStreamRef.current = yoloStream
            if (localVideoRef.current) {
                localVideoRef.current.srcObject = previewStream
                await localVideoRef.current.play().catch(() => {})
            }
            if (yoloVideoRef.current) {
                yoloVideoRef.current.srcObject = yoloStream
                await yoloVideoRef.current.play().catch(() => {})
            }

            ensureRequestIsCurrent()
            setIsLocalStreaming(true)

            if (config.syncMode && socket && isConnected) {
                setTimeout(() => {
                    startServerStream(activeYoloCamera)
                }, 500)
            }
        } catch (cameraError) {
            permissionStream?.getTracks().forEach(track => track.stop())
            previewStream?.getTracks().forEach(track => track.stop())
            yoloStream?.getTracks().forEach(track => track.stop())
            if (localVideoRef.current) localVideoRef.current.srcObject = null
            if (yoloVideoRef.current) yoloVideoRef.current.srcObject = null
            streamRef.current = null
            yoloStreamRef.current = null

            if (cameraError?.name === 'AbortError') return
            setIsLocalStreaming(false)

            const message = cameraError?.name === 'NotAllowedError' || cameraError?.name === 'SecurityError'
                ? 'Bạn chưa cấp quyền camera. Hãy cho phép camera trong thanh địa chỉ rồi thử lại.'
                : `Không thể mở hai camera: ${cameraError.message}`
            setError(message)
            console.error('Camera error:', cameraError)
        }
    }, [config.maxFps, selectedPreviewCamera, selectedCamera, config.syncMode, socket, isConnected])
    const stopLocalStream = useCallback(() => {
        cameraRequestRef.current += 1
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop())
            streamRef.current = null
        }
        if (yoloStreamRef.current) {
            yoloStreamRef.current.getTracks().forEach(track => track.stop())
            yoloStreamRef.current = null
        }
        if (localVideoRef.current) localVideoRef.current.srcObject = null
        if (yoloVideoRef.current) yoloVideoRef.current.srcObject = null
        setIsLocalStreaming(false)

        if (config.syncMode) {
            stopServerStream()
        }
    }, [config.syncMode])

    const startServerStream = useCallback((camera = null) => {
        if (!socket || !isConnected) {
            setError('Chưa kết nối đến server. Vui lòng kiểm tra kết nối.')
            return
        }
        socket.emit('join', roomId)
        setTimeout(() => {
            socket.emit('start_stream', {
                room_id: roomId,
                source: YOLO_CAMERA_SOURCE,
                camera_id: camera?.deviceId || availableCameras.find(item => item.index === selectedCamera)?.deviceId || '',
                camera_label: camera?.label || availableCameras.find(item => item.index === selectedCamera)?.label || ''
            })
        }, 200)
        setIsServerStreaming(true)
        setError(null)
    }, [socket, isConnected, roomId, selectedCamera, availableCameras])

    const stopServerStream = useCallback(() => {
        if (!socket) return
        socket.emit('stop_stream', { room_id: roomId })
        setIsServerStreaming(false)
        setReceivedFrame(null)
    }, [socket, roomId])

    const handlePreviewCameraChange = useCallback(async (newCameraIndex) => {
        setSelectedPreviewCamera(newCameraIndex)
        setError(null)
        if (isLocalStreaming) {
            await startLocalCamera(newCameraIndex, selectedCamera)
        }
    }, [isLocalStreaming, selectedCamera, startLocalCamera])

    const handleYoloCameraChange = useCallback(async (newCameraIndex) => {
        setSelectedCamera(newCameraIndex)
        setError(null)
        if (isLocalStreaming) {
            await startLocalCamera(selectedPreviewCamera, newCameraIndex)
        }
    }, [isLocalStreaming, selectedPreviewCamera, startLocalCamera])
    useEffect(() => {
        if (detections && detections.length > 0) {
            setTotalDetections(prev => prev + detections.length)
        }
    }, [detections])

    useEffect(() => {
        if (arrival && arrival.inference_ms) {
            setLastInferenceTime(arrival.inference_ms)
        }
    }, [arrival])

    useEffect(() => {
        if (!isServerStreaming || !isLocalStreaming || !socket || !isConnected) return undefined

        const canvas = document.createElement('canvas')
        canvas.width = 640
        canvas.height = 360
        const context = canvas.getContext('2d')
        const yoloCamera = availableCameras.find(camera => camera.index === selectedCamera)
        const interval = setInterval(() => {
            const video = yoloVideoRef.current
            if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return

            context.drawImage(video, 0, 0, canvas.width, canvas.height)
            socket.emit('client_frame', {
                image: canvas.toDataURL('image/jpeg', 0.65),
                room_id: roomId,
                camera_id: yoloCamera?.deviceId || '',
                camera_label: yoloCamera?.label || ''
            })
        }, 1000 / Math.min(config.maxFps || 15, 15))

        return () => clearInterval(interval)
    }, [isServerStreaming, isLocalStreaming, socket, isConnected, roomId, config.maxFps, availableCameras, selectedCamera])

    useEffect(() => {
        return () => {
            if (capturingWindowRef.current) {
                clearTimeout(capturingWindowRef.current);
            }
            stopServerStream()
            stopLocalStream()
        }
    }, [stopServerStream, stopLocalStream])

    const debugInfo = () => {
        const info = {
            socket: !!socket,
            socketId: socket?.id,
            connected: isConnected,
            roomId: roomId,
            serverStreaming: isServerStreaming,
            localStreaming: isLocalStreaming,
            selectedCamera: selectedCamera,
            availableCameras: availableCameras.length,
            syncMode: config.syncMode,
            autoDetectPlate: config.autoDetectPlate,
            hasFrame: !!receivedFrame,
            frameLength: receivedFrame?.length || 0,
            detections: detections.length,
            notifications: notificationsEnabled,
            permission: notificationPermission,
            serverUrl: serverUrl,
            plateDetectionUrl: plateDetectionUrl,
            catchEvents: catchEventHistory.length,
            lastPlate: lastPlateResult?.plate
        }
        console.log('Debug Info:', info)
        if (socket) {
            socket.emit('app_ping', { test: true, timestamp: Date.now() })
            socket.emit('join', roomId)
            socket.emit('get_stream_status', { room_id: roomId })
        }
        return info
    }

    const testCatchEvent = async () => {
        const mockEventData = {
            room_id: roomId,
            count: 2,
            detections: [
                {
                    class_name: "car",
                    confidence: 0.89,
                    bbox: [100, 150, 300, 400],
                    area: 50000
                },
                {
                    class_name: "truck",
                    confidence: 0.76,
                    bbox: [400, 200, 600, 500],
                    area: 60000
                }
            ],
            inference_ms: 45.6
        };
        await runCatchEvent(mockEventData);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-slate-800 mb-2 flex items-center justify-center gap-3">
                        <Car className="text-blue-600" size={40} />
                        SmartGate – YOLO + Plate Detection
                    </h1>
                    <p className="text-slate-600">Hệ thống phát hiện xe và nhận dạng biển số tự động</p>
                    <div className="text-sm text-slate-500 mt-2">
                        YOLO Server: <code className="bg-slate-200 px-2 py-1 rounded">{serverUrl}</code>
                        {' | '}
                        Plate Server: <code className="bg-slate-200 px-2 py-1 rounded">{plateDetectionUrl}</code>
                    </div>
                </div>

                <canvas ref={canvasRef} style={{ display: 'none' }} />
                <video ref={yoloVideoRef} autoPlay playsInline muted style={{ display: 'none' }} />

                <div className="bg-white rounded-xl shadow-sm p-4 mb-6 border border-slate-200">
                    <div className="flex flex-wrap gap-4 items-center justify-between">
                        <div className="flex gap-4 items-center flex-wrap">
                            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isConnected ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                                {isConnected ? <Wifi size={16} /> : <WifiOff size={16} />}
                                <span className="font-medium text-sm">{isConnected ? 'Kết nối' : 'Mất kết nối'}</span>
                            </div>

                            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${config.syncMode ? 'bg-purple-50 text-purple-700 border border-purple-200' : 'bg-gray-50 text-gray-600 border border-gray-200'}`}>
                                <Monitor size={16} />
                                <span className="font-medium text-sm">{config.syncMode ? 'Đồng bộ ON' : 'Đồng bộ OFF'}</span>
                            </div>

                            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 text-blue-700 border border-blue-200`}>
                                <Webcam size={16} />
                                <span className="font-medium text-sm">Camera {parseInt(selectedCamera) + 1}</span>
                            </div>

                            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${config.autoDetectPlate ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'bg-gray-50 text-gray-600 border border-gray-200'}`}>
                                <span className="font-medium text-sm">{config.autoDetectPlate ? 'Auto Plate ON' : 'Auto Plate OFF'}</span>
                            </div>

                            {isCapturingWindow && (
                                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg">
                                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                                    <span className="font-medium text-sm">Đang tìm frame tốt nhất... ({(bestFrameBuffer.confidence * 100).toFixed(1)}%)</span>
                                </div>
                            )}

                            {isDetectingPlate && (
                                <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-lg">
                                    <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                                    <span className="font-medium text-sm">Đang detect biển số...</span>
                                </div>
                            )}

                            {(isServerStreaming || isLocalStreaming) && (
                                <div className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg">
                                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                                    <span className="font-medium text-sm">LIVE</span>
                                </div>
                            )}
                        </div>

                        <div className="flex gap-6 text-sm text-slate-600">
                            <div className="flex items-center gap-1">
                                <Car size={14} />
                                <span className="font-medium">{totalDetections}</span>
                                <span>phát hiện</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <span className="font-medium text-orange-600">{catchEventHistory.length}</span>
                                <span>xe vào</span>
                            </div>
                            {lastPlateResult?.plate && (
                                <div className="flex items-center gap-1">
                                    <span className="font-medium text-indigo-600">{lastPlateResult.plate}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-slate-200">
                    <div className="flex flex-wrap gap-3 items-center justify-between">
                        <div className="flex flex-wrap gap-3">
                            <div className="relative">
                                <button
                                    onClick={() => setShowPreviewCameraSelector(!showPreviewCameraSelector)}
                                    className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium"
                                >
                                    <Video size={18} />
                                    Camera trái: {availableCameras.find(camera => camera.index === selectedPreviewCamera)?.label || `Camera ${parseInt(selectedPreviewCamera) + 1}`}
                                </button>
                                {showPreviewCameraSelector && (
                                    <div className="absolute top-full left-0 mt-2 bg-white border border-slate-200 rounded-lg shadow-lg z-20 min-w-64">
                                        {availableCameras.map((camera) => (
                                            <button
                                                key={camera.index}
                                                onClick={() => {
                                                    handlePreviewCameraChange(camera.index)
                                                    setShowPreviewCameraSelector(false)
                                                }}
                                                className={`w-full text-left px-4 py-2 hover:bg-slate-50 ${selectedPreviewCamera === camera.index ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-700'} first:rounded-t-lg last:rounded-b-lg`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <Webcam size={16} />
                                                    <span>{camera.label}</span>
                                                    {selectedPreviewCamera === camera.index && <CheckCircle size={16} className="ml-auto" />}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="relative">
                                <button
                                    onClick={() => setShowYoloCameraSelector(!showYoloCameraSelector)}
                                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                                >
                                    <Zap size={18} />
                                    Camera YOLO: {availableCameras.find(camera => camera.index === selectedCamera)?.label || `Camera ${parseInt(selectedCamera) + 1}`}
                                </button>
                                {showYoloCameraSelector && (
                                    <div className="absolute top-full left-0 mt-2 bg-white border border-slate-200 rounded-lg shadow-lg z-20 min-w-64">
                                        {availableCameras.map((camera) => (
                                            <button
                                                key={camera.index}
                                                onClick={() => {
                                                    handleYoloCameraChange(camera.index)
                                                    setShowYoloCameraSelector(false)
                                                }}
                                                className={`w-full text-left px-4 py-2 hover:bg-slate-50 ${selectedCamera === camera.index ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-slate-700'} first:rounded-t-lg last:rounded-b-lg`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <Webcam size={16} />
                                                    <span>{camera.label}</span>
                                                    {selectedCamera === camera.index && <CheckCircle size={16} className="ml-auto" />}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={() => startLocalCamera()}
                                disabled={isLocalStreaming}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors font-medium"
                            >
                                <Camera size={18} />
                                {isLocalStreaming ? 'Hai camera đang bật' : 'Bật 2 Camera'}
                            </button>

                            {!config.syncMode && (
                                <button
                                    onClick={isServerStreaming ? stopServerStream : () => startServerStream()}
                                    disabled={!isConnected}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed ${isServerStreaming ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-green-600 text-white hover:bg-green-700'}`}
                                >
                                    {isServerStreaming ? <Square size={18} /> : <Play size={18} />}
                                    {isServerStreaming ? 'Dừng YOLO' : 'Bắt đầu YOLO'}
                                </button>
                            )}

                            <button
                                onClick={() => captureAndDetectPlate('manual-button')}
                                disabled={!receivedFrame || isDetectingPlate}
                                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors font-medium"
                            >
                                <Camera size={18} />
                                {isDetectingPlate ? 'Đang detect...' : 'Detect Biển số (Cam Phải)'}
                            </button>

                            <button
                                onClick={testCatchEvent}
                                className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium"
                            >
                                Test Catch Event
                            </button>

                            <button
                                onClick={stopLocalStream}
                                disabled={!isLocalStreaming}
                                className="flex items-center gap-2 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors font-medium"
                            >
                                <Square size={18} />
                                Dừng Camera
                            </button>

                            <button
                                onClick={debugInfo}
                                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                            >
                                Debug
                            </button>
                        </div>

                        <button
                            onClick={() => setShowSettings(!showSettings)}
                            className="flex items-center gap-2 px-3 py-2 text-slate-600 hover:text-slate-800 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                        >
                            <Settings size={18} />
                            Cài đặt
                        </button>
                    </div>
                </div>

                {showCatchEventModal && latestCatchEvent && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-xl font-bold text-slate-800">Xe Vào Phát Hiện!</h3>
                                    <button
                                        onClick={() => setShowCatchEventModal(false)}
                                        className="text-slate-400 hover:text-slate-600 p-1"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>

                                <div className="grid md:grid-cols-2 gap-6">
                                    <div className="space-y-4">
                                        <div className="bg-slate-100 rounded-lg h-64 flex items-center justify-center border-2 border-slate-300">
                                            <div className="text-center text-slate-500">
                                                <div className="text-4xl mb-2">📷</div>
                                                <div className="text-sm">Ảnh xe bắt được</div>
                                            </div>
                                        </div>

                                        {latestCatchEvent.plate && (
                                            <div className="bg-indigo-50 border-2 border-indigo-300 rounded-lg p-4">
                                                <h4 className="font-bold text-indigo-800 mb-2 text-sm">Biển số xe:</h4>
                                                <div className="text-xl font-bold text-indigo-900 text-center py-2 bg-white rounded border-2 border-indigo-400">
                                                    {latestCatchEvent.plate}
                                                </div>
                                                {latestCatchEvent.plateImage && (
                                                    <div className="mt-3">
                                                        <img
                                                            src={`data:image/jpeg;base64,${latestCatchEvent.plateImage}`}
                                                            alt="Processed plate"
                                                            className="w-full rounded border border-indigo-200"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="space-y-4">
                                        <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
                                            <div className="space-y-3">
                                                <div>
                                                    <span className="font-semibold text-green-800 text-sm">Thời gian:</span>
                                                    <div className="text-green-900 font-medium text-lg">{latestCatchEvent.timestamp}</div>
                                                </div>
                                                <div>
                                                    <span className="font-semibold text-green-800 text-sm">Số xe:</span>
                                                    <div className="text-green-900 font-bold text-lg">{latestCatchEvent.vehicleCount} chiếc</div>
                                                </div>
                                                <div>
                                                    <span className="font-semibold text-green-800 text-sm">Camera:</span>
                                                    <div className="text-green-900 font-medium">Cam {parseInt(selectedCamera) + 1}</div>
                                                </div>
                                                <div>
                                                    <span className="font-semibold text-green-800 text-sm">Xử lý:</span>
                                                    <div className="text-green-900 font-medium">{latestCatchEvent.inferenceTime.toFixed(1)}ms</div>
                                                </div>
                                            </div>
                                        </div>

                                        {!latestCatchEvent.plate && config.autoDetectPlate && (
                                            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-3 text-sm text-yellow-800">
                                                Không phát hiện biển số
                                            </div>
                                        )}

                                        {latestCatchEvent.detections.length > 0 && (
                                            <div>
                                                <h4 className="font-semibold text-slate-800 mb-3">Chi tiết các xe:</h4>
                                                <div className="space-y-2">
                                                    {latestCatchEvent.detections.map((det, idx) => (
                                                        <div key={idx} className="bg-slate-50 rounded-lg p-3 border border-slate-300">
                                                            <div className="flex items-center justify-between mb-2">
                                                                <span className="font-semibold text-slate-800 capitalize">
                                                                    {det.class_name}
                                                                </span>
                                                                <span className="text-sm text-green-700 font-bold bg-green-100 px-3 py-1 rounded-full">
                                                                    {(det.confidence * 100).toFixed(1)}%
                                                                </span>
                                                            </div>
                                                            <div className="text-sm text-slate-600">
                                                                <span>Diện tích: {det.area?.toLocaleString()}px²</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <button
                                            onClick={() => setShowCatchEventModal(false)}
                                            className="w-full px-4 py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors font-semibold"
                                        >
                                            Đóng
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {showSettings && (
                    <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-slate-200">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-slate-800">Cài đặt hệ thống</h3>
                            <button onClick={() => setShowSettings(false)} className="text-slate-400 hover:text-slate-600 p-1">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="grid md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Max FPS: <span className="text-blue-600 font-semibold">{config.maxFps}</span>
                                </label>
                                <input
                                    type="range" min="10" max="60" value={config.maxFps}
                                    onChange={(e) => setConfig(prev => ({ ...prev, maxFps: parseInt(e.target.value) }))}
                                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                                />
                                <div className="flex justify-between text-xs text-slate-500 mt-1"><span>10</span><span>60</span></div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Confidence: <span className="text-green-600 font-semibold">{config.confidence}</span>
                                </label>
                                <input
                                    type="range" min="0.1" max="0.9" step="0.05" value={config.confidence}
                                    onChange={(e) => setConfig(prev => ({ ...prev, confidence: parseFloat(e.target.value) }))}
                                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                                />
                                <div className="flex justify-between text-xs text-slate-500 mt-1"><span>0.1</span><span>0.9</span></div>
                            </div>

                            <div className="md:col-span-2">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="flex items-center justify-between">
                                        <label className="text-sm font-medium text-slate-700">Âm thanh thông báo</label>
                                        <button
                                            onClick={() => setConfig(prev => ({ ...prev, enableSound: !prev.enableSound }))}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.enableSound ? 'bg-blue-600' : 'bg-slate-200'}`}
                                        >
                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.enableSound ? 'translate-x-6' : 'translate-x-1'}`} />
                                        </button>
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <label className="text-sm font-medium text-slate-700">Cảnh báo xe vào</label>
                                        <button
                                            onClick={() => setConfig(prev => ({ ...prev, enableCatchEventAlert: !prev.enableCatchEventAlert }))}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.enableCatchEventAlert ? 'bg-orange-600' : 'bg-slate-200'}`}
                                        >
                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.enableCatchEventAlert ? 'translate-x-6' : 'translate-x-1'}`} />
                                        </button>
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <label className="text-sm font-medium text-slate-700">Đồng bộ camera</label>
                                        <button
                                            onClick={() => setConfig(prev => ({ ...prev, syncMode: !prev.syncMode }))}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.syncMode ? 'bg-purple-600' : 'bg-slate-200'}`}
                                        >
                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.syncMode ? 'translate-x-6' : 'translate-x-1'}`} />
                                        </button>
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <label className="text-sm font-medium text-slate-700">Tự động detect biển số</label>
                                        <button
                                            onClick={() => setConfig(prev => ({ ...prev, autoDetectPlate: !prev.autoDetectPlate }))}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.autoDetectPlate ? 'bg-indigo-600' : 'bg-slate-200'}`}
                                        >
                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.autoDetectPlate ? 'translate-x-6' : 'translate-x-1'}`} />
                                        </button>
                                    </div>
                                </div>

                                <div className="mt-4 space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                            Confidence tối thiểu để chụp: <span className="text-indigo-600 font-semibold">{(config.minConfidenceForCapture * 100).toFixed(0)}%</span>
                                        </label>
                                        <input
                                            type="range" min="0.5" max="0.9" step="0.05" value={config.minConfidenceForCapture}
                                            onChange={(e) => setConfig(prev => ({ ...prev, minConfidenceForCapture: parseFloat(e.target.value) }))}
                                            className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                                        />
                                        <div className="flex justify-between text-xs text-slate-500 mt-1"><span>50%</span><span>90%</span></div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                            Thời gian tìm frame tốt nhất: <span className="text-blue-600 font-semibold">{(config.captureWindowDuration / 1000).toFixed(1)}s</span>
                                        </label>
                                        <input
                                            type="range" min="1000" max="5000" step="500" value={config.captureWindowDuration}
                                            onChange={(e) => setConfig(prev => ({ ...prev, captureWindowDuration: parseInt(e.target.value) }))}
                                            className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                                        />
                                        <div className="flex justify-between text-xs text-slate-500 mt-1"><span>1s</span><span>5s</span></div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                            Cooldown giữa các lần chụp: <span className="text-orange-600 font-semibold">{(config.captureCooldown / 1000).toFixed(0)}s</span>
                                        </label>
                                        <input
                                            type="range" min="3000" max="15000" step="1000" value={config.captureCooldown}
                                            onChange={(e) => setConfig(prev => ({ ...prev, captureCooldown: parseInt(e.target.value) }))}
                                            className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                                        />
                                        <div className="flex justify-between text-xs text-slate-500 mt-1"><span>3s</span><span>15s</span></div>
                                    </div>
                                </div>

                                <div className="mt-4 text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                                    <strong>Chế độ Smart Capture:</strong>
                                    <ul className="mt-2 space-y-1 text-xs list-disc list-inside">
                                        <li>Hệ thống sẽ thu thập frames trong <strong>{(config.captureWindowDuration / 1000).toFixed(1)}s</strong></li>
                                        <li>Chọn frame có confidence cao nhất (tối thiểu <strong>{(config.minConfidenceForCapture * 100).toFixed(0)}%</strong>)</li>
                                        <li>Cooldown <strong>{(config.captureCooldown / 1000).toFixed(0)}s</strong> giữa các lần chụp tránh duplicate</li>
                                        <li>Đảm bảo biển số rõ nét nhất khi xe đang di chuyển</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid lg:grid-cols-2 gap-6 mb-6">
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="p-4 border-b border-slate-100">
                            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                                <Camera size={20} />
                                Camera Trái - Nguồn gốc (Cam {parseInt(selectedPreviewCamera) + 1})
                            </h3>
                            <div className="text-xs text-slate-500 mt-1">
                                {isLocalStreaming && localVideoRef.current ?
                                    `${localVideoRef.current.videoWidth || '?'}x${localVideoRef.current.videoHeight || '?'}px` :
                                    'Không hoạt động'
                                }
                            </div>
                        </div>
                        <div className="relative bg-slate-900 aspect-video">
                            <video ref={localVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                            {isLocalStreaming && (
                                <div className="absolute top-3 left-3 bg-green-600 text-white text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
                                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                    LOCAL LIVE
                                </div>
                            )}
                            <div className="absolute top-3 right-3 bg-slate-800/90 text-white text-xs font-medium px-2 py-1 rounded-full">
                                Camera {parseInt(selectedPreviewCamera) + 1}
                            </div>
                            {!isLocalStreaming && (
                                <div className="absolute inset-0 flex items-center justify-center text-slate-400">
                                    <div className="text-center">
                                        <Camera size={48} className="mx-auto mb-3 opacity-50" />
                                        <p className="text-lg font-medium">Camera chưa được bật</p>
                                        <p className="text-sm">Chọn camera và nhấn Bật Camera</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="p-4 border-b border-slate-100">
                            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                                <Zap size={20} />
                                Camera Phải - YOLO Detection (Cam {parseInt(selectedCamera) + 1})
                            </h3>
                            <div className="text-xs text-slate-500 mt-1">
                                {receivedFrame ? 'Nhận dữ liệu từ server' : 'Chờ dữ liệu'}
                                {' | '}
                                <span className="text-indigo-600 font-medium">Phát hiện xe + Chụp biển số</span>
                            </div>
                        </div>
                        <div className="relative bg-slate-900 aspect-video">
                            {receivedFrame ? (
                                <img src={receivedFrame} alt="YOLO Processed Stream" className="w-full h-full object-cover" />
                            ) : (
                                <div className="absolute inset-0 flex items-center justify-center text-slate-400">
                                    <div className="text-center">
                                        <Zap size={48} className="mx-auto mb-3 opacity-50" />
                                        <p className="text-lg font-medium">
                                            {isConnected ?
                                                (isServerStreaming ? 'Chờ khung hình từ server' :
                                                    config.syncMode ? 'Bật camera để tự động stream' : 'Stream chưa được bắt đầu') :
                                                'Chưa kết nối server'}
                                        </p>
                                    </div>
                                </div>
                            )}
                            {isServerStreaming && receivedFrame && (
                                <div className="absolute top-3 left-3 bg-red-600 text-white text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
                                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                    YOLO LIVE
                                </div>
                            )}
                            <div className="absolute top-3 right-3 bg-slate-800/90 text-white text-xs font-medium px-2 py-1 rounded-full">
                                Camera {parseInt(selectedCamera) + 1}
                            </div>
                            {detectionCount > 0 && (
                                <div className="absolute bottom-3 right-3 bg-slate-800/90 text-white text-xs font-medium px-2 py-1 rounded-full">
                                    {detectionCount} đối tượng
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {catchEventHistory.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
                        <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                            <Car size={20} className="text-orange-600" />
                            Lịch sử sự kiện xe vào ({catchEventHistory.length})
                        </h3>
                        <div className="space-y-3 max-h-64 overflow-y-auto">
                            {catchEventHistory.map((event) => (
                                <div key={event.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                                    <div className="flex-1">
                                        <div className="font-medium text-slate-800">
                                            {event.vehicleCount} xe - {event.detections.map(d => d.class_name).join(", ")}
                                            {event.plate && <span className="ml-2 text-indigo-700 font-bold">| Biển số: {event.plate}</span>}
                                            {event.captureConfidence && <span className="ml-2 text-green-600 font-semibold">({(event.captureConfidence * 100).toFixed(1)}%)</span>}
                                        </div>
                                        <div className="text-sm text-slate-500 mt-1">
                                            {event.fullTimestamp} | {event.inferenceTime.toFixed(2)}ms | YOLO: Cam {parseInt(selectedCamera) + 1} | Biển: Cam Phải
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-2 mt-4">
                            <button
                                onClick={() => setCatchEventHistory([])}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                            >
                                Xóa lịch sử
                            </button>
                            <button
                                onClick={() => {
                                    const csvContent = "data:text/csv;charset=utf-8," +
                                        "Timestamp,Vehicle Count,Vehicle Types,Plate Number,Inference Time (ms),Camera,Room ID\n" +
                                        catchEventHistory.map(e =>
                                            `"${e.fullTimestamp}",${e.vehicleCount},"${e.detections.map(d => d.class_name).join(', ')}","${e.plate || 'N/A'}",${e.inferenceTime.toFixed(2)},Camera ${parseInt(selectedCamera) + 1},"${e.roomId}"`
                                        ).join("\n");

                                    const encodedUri = encodeURI(csvContent);
                                    const link = document.createElement("a");
                                    link.setAttribute("href", encodedUri);
                                    link.setAttribute("download", `smartgate_events_${new Date().toISOString().slice(0, 10)}.csv`);
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                }}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                            >
                                Xuất CSV
                            </button>
                        </div>
                    </div>
                )}

                <div className="text-center text-sm text-slate-500 mt-8 py-4 border-t border-slate-200">
                    <p>SmartGate YOLO + Plate Detection System | Made with React + Socket.IO + Python</p>
                    <p className="mt-1 text-xs">
                        Camera: {parseInt(selectedCamera) + 1} | Sync: {config.syncMode ? 'ON' : 'OFF'} | Auto Plate: {config.autoDetectPlate ? 'ON' : 'OFF'} | Events: {catchEventHistory.length}
                    </p>
                </div>
            </div>
        </div>
    )
}
